import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import {
    Box, Typography, Paper, Grid, Container, Button, Card, CardContent, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Avatar, CircularProgress, IconButton, Stack, Tooltip, Divider,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Class as ClassIcon,
    UploadFile as UploadFileIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    CalendarToday as CalendarTodayIcon,
    EventNote as EventNoteIcon,
    School as SchoolIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const AdminExamRoutine = () => {
    const { currentUser } = useSelector((state) => state.user);
    const navigate = useNavigate();

    const [examTypes] = useState(['first', 'second', 'mid', 'preboard', 'final']);
    const [classes, setClasses] = useState([]);
    const [selectedExamType, setSelectedExamType] = useState('');
    const [selectedClass, setSelectedClass] = useState(null);
    const [title, setTitle] = useState('');
    const [examDate, setExamDate] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [existingRoutines, setExistingRoutines] = useState([]);
    
    // Deletion state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [routineToDelete, setRoutineToDelete] = useState(null);
    
    const { showToast } = useToast();

    useEffect(() => {
        if (currentUser?._id) {
            fetchClasses();
            fetchExistingRoutines();
        }
    }, [currentUser?._id]);

    const fetchClasses = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/SclassList/${currentUser._id}`);
            if (Array.isArray(result.data)) {
                setClasses(result.data);
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchExistingRoutines = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/ExamRoutine/Admin/${currentUser._id}`);
            if (Array.isArray(result.data)) {
                setExistingRoutines(result.data);
            }
        } catch (error) {
            console.error('Error fetching exam routines:', error);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                showToast('Please select a PDF file only', 'error');
                return;
            }
            if (selectedFile.size > 10 * 1024 * 1024) {
                showToast('File size must be less than 10MB', 'error');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleSubmit = async () => {
        if (!selectedExamType || !selectedClass || !file) {
            showToast('Please select exam type, class and upload a PDF', 'warning');
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('school', currentUser._id);
        formData.append('classId', selectedClass._id);
        formData.append('examType', selectedExamType);
        formData.append('examDate', examDate);
        formData.append('title', title || `${getExamTypeLabel(selectedExamType)} Routine`);
        formData.append('file', file);

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/ExamRoutine/Create`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showToast('Exam routine uploaded successfully!', 'success');
            setSelectedClass(null);
            setSelectedExamType('');
            setTitle('');
            setExamDate('');
            setFile(null);
            fetchExistingRoutines();
        } catch (error) {
            showToast(error.response?.data?.error || 'Error uploading exam routine', 'error');
        }
        setLoading(false);
    };

    const handleDeleteClick = (id) => {
        setRoutineToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!routineToDelete) return;
        setDeleteConfirmOpen(false);
        try {
            await axios.delete(`${process.env.REACT_APP_BASE_URL}/ExamRoutine/${routineToDelete}`);
            showToast('Exam routine deleted successfully!', 'success');
            setRoutineToDelete(null);
            fetchExistingRoutines();
        } catch (error) {
            showToast('Error deleting exam routine', 'error');
            setRoutineToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setRoutineToDelete(null);
    };

    const getExamTypeLabel = (type) => {
        const labels = { first: 'First Terminal', second: 'Second Terminal', mid: 'Mid Term', preboard: 'Pre-Board', final: 'Final' };
        return labels[type] || type;
    };

    const getExamTypeColor = (type) => {
        const colors = { first: '#4caf50', second: '#2196f3', mid: '#ff9800', preboard: '#9c27b0', final: '#f44336' };
        return colors[type] || '#757575';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return typeof dateString === 'string' ? dateString.split('T')[0] : 'N/A';
    };

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>Exam Routines</Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>Publish and manage terminal examination schedules</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <StatsBadge>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Active Classes</Typography>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 800 }}>{classes.length}</Typography>
                            </StatsBadge>
                        </Stack>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 5, pb: 10 }}>
                <Grid container spacing={4}>
                    {/* Control Panel */}
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            {/* Step 1: Exam Type */}
                            <SectionCard elevation={2}>
                                <SectionHeader color="#667eea">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <EventNoteIcon />
                                        <Typography variant="subtitle1" fontWeight="700" color="white">1. Exam Type</Typography>
                                    </Stack>
                                </SectionHeader>
                                <Box sx={{ p: 3 }}>
                                    <Grid container spacing={1.5}>
                                        {examTypes.map((type) => (
                                            <Grid item xs={6} key={type}>
                                                <TypeOption 
                                                    $selected={selectedExamType === type}
                                                    onClick={() => setSelectedExamType(type)}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{getExamTypeLabel(type)}</Typography>
                                                </TypeOption>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </SectionCard>

                            {/* Step 2: Class Selection */}
                            <SectionCard elevation={2}>
                                <SectionHeader color="#4caf50">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <ClassIcon />
                                        <Typography variant="subtitle1" fontWeight="700" color="white">2. Target Class</Typography>
                                    </Stack>
                                </SectionHeader>
                                <Box sx={{ p: 2, maxHeight: '300px', overflowY: 'auto' }}>
                                    <Grid container spacing={1}>
                                        {classes.map((cls) => (
                                            <Grid item xs={4} key={cls._id}>
                                                <ClassOption 
                                                    $selected={selectedClass?._id === cls._id}
                                                    onClick={() => setSelectedClass(cls)}
                                                >
                                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{cls.sclassName}</Typography>
                                                </ClassOption>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </SectionCard>
                        </Stack>
                    </Grid>

                    {/* Upload & Management */}
                    <Grid item xs={12} lg={8}>
                        <Stack spacing={3}>
                            {/* Upload Form */}
                            <SectionCard elevation={2}>
                                <SectionHeader color="#1a237e">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <UploadFileIcon />
                                        <Typography variant="subtitle1" fontWeight="700" color="white">3. Upload Routine PDF</Typography>
                                    </Stack>
                                </SectionHeader>
                                <Box sx={{ p: 4 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField 
                                                fullWidth label="Routine Title" value={title} 
                                                onChange={(e) => setTitle(e.target.value)} 
                                                placeholder="e.g. Annual Exam Schedule 2024"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CustomNepaliDatePicker 
                                                label="Exam Start Date" 
                                                value={examDate} 
                                                onChange={(date) => setExamDate(date)} 
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <UploadBox>
                                                <input type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} id="routine-file" />
                                                <label htmlFor="routine-file">
                                                    <Box sx={{ cursor: 'pointer', py: 4 }}>
                                                        <UploadFileIcon sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                                            {file ? 'Replace PDF File' : 'Click to Browse Routine PDF'}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">Only PDF files are supported (Max 10MB)</Typography>
                                                    </Box>
                                                </label>
                                            </UploadBox>
                                            {file && (
                                                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <DescriptionIcon sx={{ color: '#16a34a' }} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>{file.name}</Typography>
                                                            <Typography variant="caption" color="textSecondary">{(file.size / 1024 / 1024).toFixed(2)} MB</Typography>
                                                        </Box>
                                                    </Stack>
                                                    <IconButton size="small" onClick={() => setFile(null)} sx={{ color: '#dc2626' }}><DeleteIcon fontSize="small" /></IconButton>
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button 
                                                fullWidth variant="contained" size="large" onClick={handleSubmit}
                                                disabled={loading || !file || !selectedClass || !selectedExamType}
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                                sx={{ py: 2, borderRadius: '12px', fontWeight: 800, textTransform: 'none', bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1b5e' } }}
                                            >
                                                {loading ? 'Publishing...' : 'Publish Routine'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </SectionCard>

                            {/* Existing Records */}
                            <SectionCard elevation={2}>
                                <SectionHeader color="#475569">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HistoryIcon />
                                        <Typography variant="subtitle1" fontWeight="700" color="white">Published Routines</Typography>
                                    </Stack>
                                </SectionHeader>
                                <TableContainer>
                                    <Table sx={{ minWidth: 700 }}>
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <StyledTableCell>Info</StyledTableCell>
                                                <StyledTableCell>Exam Date</StyledTableCell>
                                                <StyledTableCell>Type</StyledTableCell>
                                                <StyledTableCell align="center">Action</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {existingRoutines.map((routine) => (
                                                <TableRow key={routine._id} hover>
                                                    <TableCell>
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{routine.title}</Typography>
                                                            <Typography variant="caption" color="textSecondary">Class: {routine.class?.sclassName || 'N/A'}</Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <CalendarTodayIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(routine.examDate)}</Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={getExamTypeLabel(routine.examType)} 
                                                            size="small" 
                                                            sx={{ bgcolor: getExamTypeColor(routine.examType), color: 'white', fontWeight: 800, fontSize: '0.7rem' }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={1} justifyContent="center">
                                                            <Tooltip title="View PDF">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => {
                                                                        const baseUrl = process.env.REACT_APP_BASE_URL;
                                                                        const filename = routine.filePath.split(/[/\\]/).pop();
                                                                        window.open(`${baseUrl}/uploads/exam-routines/${encodeURIComponent(filename)}`, '_blank');
                                                                    }}
                                                                    sx={{ color: '#667eea', bgcolor: '#f0f4ff' }}
                                                                >
                                                                    <DescriptionIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton size="small" onClick={() => handleDeleteClick(routine._id)} sx={{ color: '#f44336', bgcolor: '#ffebee' }}><DeleteIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {existingRoutines.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                                        <EventNoteIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 1 }} />
                                                        <Typography color="textSecondary">No exam routines have been published yet.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </SectionCard>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
            >
                <DialogTitle sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569' }}>
                        Are you sure you want to delete this exam routine? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCancelDelete} sx={{ color: '#64748b', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        color="error" 
                        sx={{ borderRadius: '8px', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </PageContainer>
    );
};

export default AdminExamRoutine;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 40px;`;
const SectionCard = styled(Paper)`border-radius: 20px !important; overflow: hidden;`;
const SectionHeader = styled(Box)`padding: 12px 24px; background: ${props => props.color}; color: white;`;
const StatsBadge = styled(Box)`padding: 12px 24px; background: rgba(255,255,255,0.1); border-radius: 16px; border: 1px solid rgba(255,255,255,0.1); text-align: center;`;
const TypeOption = styled(Box)`
    padding: 16px; border-radius: 14px; text-align: center; cursor: pointer; transition: 0.3s;
    border: 2px solid ${props => props.$selected ? '#667eea' : '#e2e8f0'};
    background: ${props => props.$selected ? '#f5f7ff' : 'white'};
    color: ${props => props.$selected ? '#667eea' : '#64748b'};
    &:hover { border-color: #667eea; background: #f5f7ff; transform: translateY(-2px); }
`;
const ClassOption = styled(Box)`
    padding: 12px; border-radius: 12px; text-align: center; cursor: pointer; transition: 0.2s;
    border: 2px solid ${props => props.$selected ? '#4caf50' : '#e2e8f0'};
    background: ${props => props.$selected ? '#f0fdf4' : 'white'};
    color: ${props => props.$selected ? '#4caf50' : '#64748b'};
    &:hover { border-color: #4caf50; background: #f0fdf4; }
`;
const UploadBox = styled(Box)`
    border: 2px dashed #e2e8f0; border-radius: 20px; text-align: center; transition: 0.3s;
    &:hover { border-color: #667eea; background: #f8faff; }
`;
const StyledTableCell = styled(TableCell)`font-weight: 700 !important; color: #475569 !important; padding: 16px !important;`;
